import type { QueryClient } from "@tanstack/react-query";
import { QueryKey } from "../../../utils/api";
import {
  useSpecificationStateStore,
  type Change,
  type ChangeTarget,
  makeAttributeChangeTarget,
} from "../conditions/specificationState";

/**
 * Teardown state helpers
 *
 * Goal:
 * - Keep Teardown flag editing logic self-contained and teardown-specific.
 * - Avoid relying on condition BOM semantics beyond the existing change-tracking primitives.
 * - Provide a reliable "persist without refresh" experience by committing changes into
 *   the React Query cache after save, even if the backend/FormSpec read path is stale.
 */

/**
 * The teardown item is treated as the root of an item_tree.
 * (This matches the existing modal behavior where root path "" points at teardown item.)
 */
export const TEARDOWN_ITEM_PATH = "";

/**
 * Build a ChangeTarget for a teardown flag attribute edit.
 */
export const makeTeardownAttributeTarget = (
  attributeName: string,
  path: string = TEARDOWN_ITEM_PATH
): ChangeTarget => makeAttributeChangeTarget(path, attributeName);

/**
 * Derive the "next value" for an attribute based on change operation.
 * - delete => undefined (meaning remove/clear)
 * - update/add => change.currentValue
 */
const getChangeNextValue = (change: Change): any => {
  if (change.operation === "delete") return undefined;
  return change.currentValue;
};

/**
 * Apply a single attribute change to an attributes object immutably.
 */
const applyAttributeChange = (
  attrs: Record<string, any>,
  attributeName: string,
  change: Change
): Record<string, any> => {
  const next = { ...attrs };
  const nextValue = getChangeNextValue(change);

  if (nextValue === undefined) {
    // “delete” semantics: remove the key entirely for best compatibility
    delete next[attributeName];
  } else {
    next[attributeName] = nextValue;
  }

  return next;
};

/**
 * Commit all teardown attribute changes from the change store into a FormSpec item payload.
 *
 * This is useful for making the UI reflect the state the user just saved, without relying on
 * immediate read-your-writes behavior of the backend/FormSpec GET endpoint.
 *
 * IMPORTANT:
 * - This only applies attribute changes at the teardown root path.
 * - It intentionally ignores BOM/process edits, since TeardownFlagsModal only edits attributes.
 */
export const buildCommittedTeardownItem = (
  existing: ItemWithBOM,
  changes: Array<Change | null | undefined>,
  teardownItemPath: string = TEARDOWN_ITEM_PATH
): ItemWithBOM => {
  const baseAttrs: Record<string, any> = {
    ...((existing?.item as any)?.attributes || {}),
  };

  // Apply all attribute changes that target the teardown root path
  const committedAttrs = (changes || []).reduce((attrs, change) => {
    if (!change) return attrs;

    const target = change.target;
    if (!target || target.type !== "attribute") return attrs;
    if (target.path !== teardownItemPath) return attrs;

    return applyAttributeChange(attrs, target.propertyName, change);
  }, baseAttrs);

  return {
    ...existing,
    item: {
      ...(existing?.item as any),
      attributes: committedAttrs,
    },
  };
};

/**
 * Extract ALL attribute changes for the teardown root path from the Zustand store.
 *
 * We intentionally do not import the entire store here to keep this file "helpers-only".
 * Consumers can pass `getChange` and the set of attribute names they care about.
 *
 * Usage:
 * - Provide the attribute names for the currently resolved fields.
 * - This returns a Change[] aligned to those names (null when no change).
 */
export const collectTeardownAttributeChanges = (
  attributeNames: string[],
  getChange: (target: ChangeTarget) => Change | null,
  teardownItemPath: string = TEARDOWN_ITEM_PATH
): Array<Change | null> => {
  return attributeNames.map((attributeName) =>
    getChange(makeTeardownAttributeTarget(attributeName, teardownItemPath))
  );
};

/**
 * Seed the teardown item_tree in the existing specification store.
 *
 * This intentionally uses the existing store’s `setCondition` entry point, because:
 * - the save mutation expects an item_tree in the same format
 * - change tracking is already implemented there
 *
 * But: all callers should treat this as a teardown-specific “seed” operation, not a condition BOM.
 */
export const seedTeardownItemTree = (teardownItem: ItemWithBOM) => {
  // The underlying store is typed for `FormspecCondition` (condition-rooted trees).
  // Teardown flags operate on a standalone ItemWithBOM-shaped tree, so we cast here
  // to reuse the existing change-tracking + save plumbing without fighting types.
  useSpecificationStateStore.getState().setCondition({
    item: teardownItem.item,
    bom: teardownItem.bom || [],
    processes: teardownItem.processes || [],
  } as any);
};

/**
 * After a successful save, update the React Query cache for `formspec/items/:uuid` so
 * UI updates immediately (even if the GET endpoint is temporarily stale).
 *
 * This does NOT replace refetching; you can still refetch afterwards to reconcile.
 */
export const commitTeardownFlagsToQueryCache = (
  queryClient: QueryClient,
  teardownItemUuid: string,
  committedTeardownItem: ItemWithBOM
) => {
  const uuid = teardownItemUuid?.trim();
  if (!uuid) return;

  const key = [QueryKey.Items, uuid, "details"];

  queryClient.setQueryData(key, committedTeardownItem);
};
